import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import { appendAuthToken } from "../utils/auth";

// Date formatters
const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    // Try DD/MM/YYYY check
    return value;
  }
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Next service date calculator: adds 1 year - 1 day to serviceDate
const calculateNextServiceDate = (serviceDateStr) => {
  if (!serviceDateStr) return "";
  let date = new Date(serviceDateStr);
  if (isNaN(date.getTime())) {
    const parts = serviceDateStr.split(/[-/]/);
    if (parts.length === 3) {
      if (parts[2].length === 4) { // DD/MM/YYYY
        date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      } else if (parts[0].length === 4) { // YYYY/MM/DD
        date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
    }
  }
  if (!isNaN(date.getTime())) {
    date.setFullYear(date.getFullYear() + 1);
    date.setDate(date.getDate() - 1);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  return "";
};

const parseDateObject = (dStr) => {
  if (!dStr) return new Date(0);
  const parts = dStr.split(/[-/]/);
  if (parts.length === 3) {
    if (parts[2].length === 4) { // DD/MM/YYYY
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    if (parts[0].length === 4) { // YYYY/MM/DD
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
  }
  const d = new Date(dStr);
  return isNaN(d.getTime()) ? new Date(0) : d;
};

export default function DashboardAlerts() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAccounts = location.pathname.startsWith("/accounts");
  const [activeTab, setActiveTab] = useState(isAccounts ? "services" : "approvals"); // "approvals" | "services" | "quotations"

  // Data states
  const [visits, setVisits] = useState([]);
  const [generalServices, setGeneralServices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingQuotation, setViewingQuotation] = useState(null);

  // Interaction states
  const [actioningVisitId, setActioningVisitId] = useState(null);
  const [approvalFinalStatus, setApprovalFinalStatus] = useState("completed");
  const [remarksInput, setRemarksInput] = useState("");
  const [actionType, setActionType] = useState(null); // "approve" | "changes"
  const [submittingAction, setSubmittingAction] = useState(false);

  // Fetch all required data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [visitsRes, gsRes, quotationsRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/visits"),
        axios.get(API_BASE_URL + "/api/general-services"),
        axios.get(API_BASE_URL + "/api/quotations")
      ]);
      setVisits(Array.isArray(visitsRes.data) ? visitsRes.data : []);
      setGeneralServices(Array.isArray(gsRes.data) ? gsRes.data : []);
      setQuotations(Array.isArray(quotationsRes.data) ? quotationsRes.data : []);
    } catch (error) {
      console.error("Failed to fetch alerts dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived datasets
  // 1. Visits Pending Approval
  const pendingVisits = useMemo(() => {
    return visits.filter(
      (v) =>
        v.approvalStatus === "Approval Pending" &&
        v.isDeleted !== true &&
        v.reportType !== "merged_final"
    );
  }, [visits]);

  // 2. Expired Quotations
  const expiredQuotations = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return quotations.filter((q) => {
      if (!q.validUntil || q.isDeleted) return false;
      const isUnresolved = ["draft", "sent"].includes(q.status);
      const expiryDate = new Date(q.validUntil);
      return isUnresolved && expiryDate < today;
    });
  }, [quotations]);

  // 3. Next Service notifications
  const nextServices = useMemo(() => {
    // A. General services next dates
    const gsUpcoming = generalServices
      .filter((gs) => gs.nextServiceDate && gs.isDeleted !== true && gs.isProcessed !== true && gs.processed !== true)
      .map((gs) => ({
        type: "General Service",
        serviceId: gs.serviceId,
        _id: gs._id,
        customerName: gs.customerName || gs.company || "Unknown",
        productName: gs.serviceName || "General Service",
        serialNo: "N/A",
        lastServiceDate: gs.serviceDate || "-",
        nextServiceDate: gs.nextServiceDate,
        originalItem: gs
      }));

    // B. Breaker products serviced in visits dynamically projected 1 year later
    const breakerUpcoming = [];
    visits.forEach((v) => {
      if (v.isDeleted !== true && v.products && Array.isArray(v.products)) {
        v.products.forEach((p) => {
          if (!p) return;
          if (p.isProcessed === true || p.processed === true) return;
          const pName = String(p.productName || p.product || "").trim();
          const category = String(p.category || "").toLowerCase();
          const isBreaker = category === "breaker" || /breaker|acb|mccb|vcb|mcb/i.test(pName);
          if (isBreaker) {
            const lastServiceDate = v.visitDate || v.date || p.visitDate || "";
            const nextDate = calculateNextServiceDate(lastServiceDate);
            if (nextDate) {
              // Avoid duplicates on serialNo if present
              if (p.serialNo && breakerUpcoming.some((item) => item.serialNo === p.serialNo)) {
                return;
              }
              breakerUpcoming.push({
                type: "Breaker Service",
                serviceId: v.visitId || v.reportId || "N/A",
                _id: v._id,
                customerName: v.customerName || v.company || "Unknown",
                productName: pName,
                serialNo: p.serialNo || "N/A",
                lastServiceDate: lastServiceDate,
                nextServiceDate: nextDate,
                originalVisit: v,
                originalProduct: p
              });
            }
          }
        });
      }
    });

    const combined = [...gsUpcoming, ...breakerUpcoming];
    const filtered = combined.filter((srv) => {
      const diffDays = Math.round((parseDateObject(srv.nextServiceDate).getTime() - new Date().getTime()) / 86400000);
      return diffDays <= 60;
    });
    filtered.sort((a, b) => parseDateObject(a.nextServiceDate) - parseDateObject(b.nextServiceDate));
    return filtered;
  }, [generalServices, visits]);

  const groupedNextServices = useMemo(() => {
    const groups = [];
    nextServices.forEach((srv) => {
      const existing = groups.find(
        (g) =>
          g.customerName === srv.customerName &&
          g.productName === srv.productName &&
          g.nextServiceDate === srv.nextServiceDate
      );
      if (existing) {
        existing.count += 1;
        existing.items.push(srv);
      } else {
        groups.push({
          type: srv.type,
          customerName: srv.customerName,
          productName: srv.productName,
          nextServiceDate: srv.nextServiceDate,
          count: 1,
          items: [srv]
        });
      }
    });
    return groups;
  }, [nextServices]);

  // Quick stats calculation
  const stats = useMemo(() => {
    return {
      pendingApprovals: pendingVisits.length,
      upcomingServicesCount: nextServices.length,
      expiredQuotationsCount: expiredQuotations.length
    };
  }, [pendingVisits, nextServices, expiredQuotations]);

  // Report Approvals actions
  const handleApproveReport = async (visit) => {
    try {
      setSubmittingAction(true);
      const approvedBy = localStorage.getItem("adminName") || "Admin";
      try {
        await axios.put(API_BASE_URL + `/api/visits/${visit._id}/approve`, {
          approvedBy,
          finalStatus: approvalFinalStatus
        });
      } catch (err) {
        // Fallback endpoint PUT request
        await axios.put(API_BASE_URL + `/api/visits/${visit._id}`, {
          ...visit,
          approvalStatus: "Approved",
          finalStatus: approvalFinalStatus,
          reviewRemarks: "",
          approvedBy,
          approvedAt: new Date().toISOString()
        });
      }
      alert("Report approved successfully!");
      setActioningVisitId(null);
      setActionType(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to approve report");
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleRequestChanges = async (visit) => {
    const remarks = String(remarksInput).trim();
    if (!remarks) {
      alert("Please enter review remarks to request changes.");
      return;
    }
    try {
      setSubmittingAction(true);
      try {
        await axios.put(API_BASE_URL + `/api/visits/${visit._id}/request-changes`, {
          reviewRemarks: remarks
        });
      } catch (err) {
        // Fallback endpoint PUT request
        await axios.put(API_BASE_URL + `/api/visits/${visit._id}`, {
          ...visit,
          approvalStatus: "Changes Requested",
          reviewRemarks: remarks,
          approvedAt: null,
          approvedBy: ""
        });
      }
      alert("Changes requested. Report sent back to engineer.");
      setActioningVisitId(null);
      setActionType(null);
      setRemarksInput("");
      loadData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to submit change request");
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleProcessServiceGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to mark these service schedules as processed?`)) {
      return;
    }
    try {
      setLoading(true);
      for (const item of group.items) {
        if (item.type === "General Service") {
          await axios.put(API_BASE_URL + `/api/general-services/${item._id}`, {
            ...item.originalItem,
            isProcessed: true
          });
        } else if (item.type === "Breaker Service") {
          const visit = item.originalVisit;
          const updatedProducts = visit.products.map((p) => {
            if (p && p.serialNo === item.originalProduct.serialNo && (p.productName === item.originalProduct.productName || p.product === item.originalProduct.product)) {
              return {
                ...p,
                isProcessed: true,
                processed: true
              };
            }
            return p;
          });
          
          await axios.put(API_BASE_URL + `/api/visits/${visit._id}`, {
            ...visit,
            products: updatedProducts,
            isProcessingProduct: true
          });
        }
      }
      alert("Services marked as processed successfully!");
      loadData();
    } catch (error) {
      console.error("Failed to process services:", error);
      alert(error.response?.data?.message || "Failed to process services");
      loadData();
    }
  };

  const handleUpdateQuotationStatus = async (quotationId, newStatus) => {
    try {
      setLoading(true);
      await axios.put(API_BASE_URL + `/api/quotations/${quotationId}/status`, { status: newStatus });
      setQuotations((prev) =>
        prev.map((q) => (q._id === quotationId ? { ...q, status: newStatus } : q))
      );
      if (viewingQuotation && viewingQuotation._id === quotationId) {
        setViewingQuotation((prev) => ({ ...prev, status: newStatus }));
      }
      loadData();
    } catch (error) {
      console.error("Failed to update quotation status:", error);
      alert(error.response?.data?.message || "Failed to update quotation status");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 lg:p-6">
      {/* Page Header */}
      <section className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-full opacity-10 pointer-events-none bg-cover bg-[url('https://lh3.googleusercontent.com/aida-public/AB6AXuDqYE1hdDlzdRt4ZdIdlRw_Eo4SXIsupnYjzAdbklaOXcKsdy3sC3QydjBahQX32OHeTx_RUithlTvKRWI6HgR5YhuJqyLJqBjMW8tK3hX1vb6qmH304sRNlKo_WnYfz5M42ukyC6bTL-ZdaiwyKHJ38NoLeR-c8adkMfDDg3yUPVVZBXZF1ck4lY-fYx3_vg3J9frX2QA7f-Ya-CtRjkiyvi_3HloFbm3sTs-aFMDN2TDumabZUAZm3N3UFUqMEstCEYLEyXQSdDk')]"></div>
        <div className="relative z-10">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 block mb-2">Admin Dashboard Panel</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Approvals &amp; Alerts Hub</h1>
          <p className="mt-2 text-slate-300 max-w-3xl text-sm leading-relaxed">
            Consolidated workspace to review pending engineer reports, track upcoming service alerts, and manage expired quotation statuses.
          </p>
        </div>
      </section>

      {/* Overview Metric Bento Cards */}
      <section className={`grid grid-cols-1 md:grid-cols-2 ${isAccounts ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-4`}>
        {!isAccounts && (
          <button
            onClick={() => setActiveTab("approvals")}
            className={`p-5 rounded-2xl border text-left transition-all ${
              activeTab === "approvals"
                ? "bg-amber-50 border-amber-300 shadow-md ring-2 ring-amber-200"
                : "bg-white border-slate-200 hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Pending Approvals</span>
              <span className="material-symbols-outlined text-amber-600 bg-amber-100 p-2 rounded-xl text-lg">rate_review</span>
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900">{loading ? "..." : stats.pendingApprovals}</h2>
            <p className="text-xs text-slate-500 mt-2">Reports awaiting verification</p>
          </button>
        )}

        <button
          onClick={() => setActiveTab("services")}
          className={`p-5 rounded-2xl border text-left transition-all ${
            activeTab === "services"
              ? "bg-blue-50 border-blue-300 shadow-md ring-2 ring-blue-200"
              : "bg-white border-slate-200 hover:shadow-md"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Next Service Alert</span>
            <span className="material-symbols-outlined text-blue-600 bg-blue-100 p-2 rounded-xl text-lg">notifications_active</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900">{loading ? "..." : stats.upcomingServicesCount}</h2>
          <p className="text-xs text-slate-500 mt-2">Scheduled within 60 days</p>
        </button>

        <button
          onClick={() => setActiveTab("quotations")}
          className={`p-5 rounded-2xl border text-left transition-all ${
            activeTab === "quotations"
              ? "bg-purple-50 border-purple-300 shadow-md ring-2 ring-purple-200"
              : "bg-white border-slate-200 hover:shadow-md"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Expired Quotations</span>
            <span className="material-symbols-outlined text-purple-600 bg-purple-100 p-2 rounded-xl text-lg">hourglass_empty</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900">{loading ? "..." : stats.expiredQuotationsCount}</h2>
          <p className="text-xs text-slate-500 mt-2">Validity expired, status pending</p>
        </button>
      </section>

      {/* Tabs Layout */}
      <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Navigation Tabs Header */}
        <div className="flex border-b border-slate-200 bg-slate-50/50 p-2 gap-2">
          {!isAccounts && (
            <button
              onClick={() => setActiveTab("approvals")}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
                activeTab === "approvals"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              📋 Approvals Awaiting ({pendingVisits.length})
            </button>
          )}
          <button
            onClick={() => setActiveTab("services")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
              activeTab === "services"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            ⏰ Service Notifications ({groupedNextServices.length})
          </button>
          <button
            onClick={() => setActiveTab("quotations")}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
              activeTab === "quotations"
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            📄 Quotation Alerts ({expiredQuotations.length})
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="py-16 text-center text-slate-500 font-semibold flex flex-col items-center justify-center gap-3">
              <span className="material-symbols-outlined text-4xl animate-spin text-slate-400">sync</span>
              <span>Loading details from database...</span>
            </div>
          ) : (
            <>
              {/* TAB 1: REPORTS PENDING APPROVAL */}
              {activeTab === "approvals" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">Service Reports Waiting Approval</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Visits completed by engineers awaiting admin clearance</p>
                    </div>
                    <span className="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full">
                      {pendingVisits.length} Pending
                    </span>
                  </div>

                  {pendingVisits.length === 0 ? (
                    <div className="py-12 text-center text-slate-500 border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                      <span className="material-symbols-outlined text-4xl text-slate-400 mb-2">check_circle</span>
                      <p className="font-semibold text-sm">All reports approved! You're caught up.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingVisits.map((visit) => (
                        <div
                          key={visit._id}
                          className="border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow bg-[linear-gradient(180deg,_#fafaf9,_#ffffff)]"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            {/* Summary info */}
                            <div className="space-y-1 flex-1 min-w-0">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h4 className="text-sm font-bold text-slate-900">{visit.reportId || visit.visitId}</h4>
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-blue-50 text-blue-700">
                                  {visit.complaintRefId || visit.complaintId || "General Service"}
                                </span>
                                <span className="text-xs text-slate-500">
                                  Visit: {formatDate(visit.visitDate || visit.date)}
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-slate-950 truncate">
                                {visit.customerName || visit.company}
                              </p>
                              <div className="text-xs text-slate-500 flex items-center gap-4 flex-wrap">
                                <span>Engineer: <strong className="text-slate-700">{visit.engineer}</strong></span>
                                <span>Location: <strong className="text-slate-700">{visit.city || "-"}</strong></span>
                                {visit.products && visit.products.length > 0 && (
                                  <span>Products: <strong className="text-slate-700">{visit.products.length}</strong></span>
                                )}
                              </div>
                            </div>

                            {/* Actions bar */}
                            <div className="flex items-center gap-2 self-start lg:self-center shrink-0">
                              <button
                                onClick={() => navigate(isAccounts ? `/accounts/reports/${visit._id}` : `/reports/${visit._id}`)}
                                className="px-3.5 py-2 text-xs font-bold border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors text-slate-700"
                              >
                                View Details
                              </button>
                              
                              {actioningVisitId === visit._id ? (
                                <button
                                  onClick={() => { setActioningVisitId(null); setActionType(null); }}
                                  className="px-3.5 py-2 text-xs font-bold border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700"
                                >
                                  Cancel
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => { setActioningVisitId(visit._id); setActionType("approve"); }}
                                    className="px-3.5 py-2 text-xs font-bold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 shadow-sm hover:shadow transition-all"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => { setActioningVisitId(visit._id); setActionType("changes"); }}
                                    className="px-3.5 py-2 text-xs font-bold bg-rose-50 text-rose-700 rounded-xl hover:bg-rose-100 transition-colors"
                                  >
                                    Send Back
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Action Expand Form Drawer */}
                          {actioningVisitId === visit._id && (
                            <div className="mt-4 pt-4 border-t border-slate-200/60 bg-slate-50/50 p-4 rounded-xl space-y-4 transition-all">
                              {actionType === "approve" ? (
                                <div className="space-y-3">
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                                      Select Final Workflow Status:
                                    </label>
                                    <select
                                      value={approvalFinalStatus}
                                      onChange={(e) => setApprovalFinalStatus(e.target.value)}
                                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none focus:border-blue-500"
                                    >
                                      <option value="completed">Completed</option>
                                      <option value="pending">Pending</option>
                                      <option value="re-visit">Re-Visit</option>
                                      <option value="need to provide quote">Need to provide quote</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <button
                                      disabled={submittingAction}
                                      onClick={() => handleApproveReport(visit)}
                                      className="px-4 py-2 text-xs font-bold bg-slate-900 hover:bg-black text-white rounded-xl disabled:opacity-50"
                                    >
                                      {submittingAction ? "Approving..." : "Confirm & Approve"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                                      Review Remarks / Request Changes:
                                    </label>
                                    <textarea
                                      rows="3"
                                      value={remarksInput}
                                      onChange={(e) => setRemarksInput(e.target.value)}
                                      placeholder="State what needs to be changed/verified by the engineer..."
                                      className="w-full text-xs p-3 border border-slate-300 bg-white rounded-xl outline-none focus:border-blue-500"
                                    />
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <button
                                      disabled={submittingAction}
                                      onClick={() => handleRequestChanges(visit)}
                                      className="px-4 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl disabled:opacity-50"
                                    >
                                      {submittingAction ? "Submitting..." : "Send Remarks to Engineer"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: NEXT SERVICE NOTIFICATIONS */}
              {activeTab === "services" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">Upcoming Services &amp; Maintenance Timeline</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Schedules compiled from General Services and dyn-projected breaker preventative cycles</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1.5 rounded-full">
                      {groupedNextServices.length} Total Schedules
                    </span>
                  </div>

                  {groupedNextServices.length === 0 ? (
                    <div className="py-12 text-center text-slate-500 border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                      <span className="material-symbols-outlined text-4xl text-slate-400 mb-2">date_range</span>
                      <p className="font-semibold text-sm">No service notifications scheduled.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                      <table className="w-full text-left min-w-[700px]">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-widest border-b border-slate-200">
                          <tr>
                            <th className="px-5 py-3">Due Date</th>
                            <th className="px-5 py-3">Category</th>
                            <th className="px-5 py-3">Customer / Company</th>
                            <th className="px-5 py-3">Product Name</th>
                            <th className="px-5 py-3">No. of Products</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-150 text-sm">
                          {groupedNextServices.map((srv, idx) => {
                            const diffDays = Math.round((parseDateObject(srv.nextServiceDate).getTime() - new Date().getTime()) / 86400000);
                            
                            let statusBadge = "bg-emerald-100 text-emerald-800";
                            let statusText = `${diffDays} Days Left`;
                            if (diffDays < 0) {
                              statusBadge = "bg-rose-100 text-rose-800 font-bold";
                              statusText = "Overdue";
                            } else if (diffDays === 0) {
                              statusBadge = "bg-amber-150 text-amber-800 font-bold ring-2 ring-amber-200 animate-pulse";
                              statusText = "Due Today";
                            } else if (diffDays <= 7) {
                              statusBadge = "bg-amber-100 text-amber-850 font-bold";
                            }

                            return (
                              <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                                <td className="px-5 py-3.5 font-bold text-slate-900">
                                  {srv.nextServiceDate}
                                </td>
                                <td className="px-5 py-3.5">
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                    srv.type === "General Service" ? "bg-purple-100 text-purple-700" : "bg-sky-100 text-sky-700"
                                  }`}>
                                    {srv.type}
                                  </span>
                                </td>
                                <td className="px-5 py-3.5 font-semibold text-slate-805">
                                  {srv.customerName}
                                </td>
                                <td className="px-5 py-3.5 text-slate-600">
                                  {srv.productName}
                                </td>
                                <td className="px-5 py-3.5 text-xs text-slate-700 font-bold">
                                  {srv.count}
                                </td>
                                <td className="px-5 py-3.5">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge}`}>
                                    {statusText}
                                  </span>
                                </td>
                                <td className="px-5 py-3.5 text-right">
                                  <button
                                    onClick={() => handleProcessServiceGroup(srv)}
                                    className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-800 text-xs font-bold rounded-lg transition-colors inline-flex items-center gap-1"
                                  >
                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                    Processed
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 3: QUOTATION ALERTS */}
              {activeTab === "quotations" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">Expired Quotations Status Update</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Quotations whose validity date has passed and require status updates</p>
                    </div>
                    <span className="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1.5 rounded-full">
                      {expiredQuotations.length} Pending
                    </span>
                  </div>

                  {expiredQuotations.length === 0 ? (
                    <div className="py-12 text-center text-slate-500 border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                      <span className="material-symbols-outlined text-4xl text-slate-400 mb-2">check_circle</span>
                      <p className="font-semibold text-sm">All quotation statuses are up to date!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {expiredQuotations.map((quote) => (
                        <div
                          key={quote._id}
                          className="border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow bg-[linear-gradient(180deg,_#fafaf9,_#ffffff)]"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="space-y-1 flex-1 min-w-0">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h4 className="text-sm font-bold text-slate-900">{quote.quotationNumber}</h4>
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-50 text-rose-700">
                                  Expired on {formatDate(quote.validUntil)}
                                </span>
                                <span className="text-xs text-slate-500">
                                  Created: {formatDate(quote.quotationDate)}
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-slate-950 truncate">
                                {quote.customerName}
                              </p>
                              <div className="text-xs text-slate-500 flex items-center gap-4 flex-wrap">
                                <span>Subject: <strong className="text-slate-700">{quote.subject || "-"}</strong></span>
                                <span>Current Status: <strong className="text-slate-700 capitalize">{quote.status}</strong></span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 self-start lg:self-center shrink-0 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setViewingQuotation(quote)}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold border border-slate-300 rounded-xl hover:bg-slate-100 text-slate-700 bg-white transition-colors shadow-sm"
                                title="View Quotation Details"
                              >
                                <span className="material-symbols-outlined text-[16px]">visibility</span>
                                View Quote
                              </button>
                              <select
                                value={quote.status || "sent"}
                                onChange={(e) => handleUpdateQuotationStatus(quote._id, e.target.value)}
                                className="px-2.5 py-2 text-xs font-semibold border border-slate-300 rounded-xl bg-white text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                title="Update Status"
                              >
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="accepted">Accepted (Won)</option>
                                <option value="rejected">Rejected (Lost)</option>
                                <option value="expired">Expired</option>
                                <option value="invoiced">Invoiced</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Quotation Details Modal */}
      {viewingQuotation && (
        <QuotationViewerModal
          quotation={viewingQuotation}
          onClose={() => setViewingQuotation(null)}
          onStatusChange={handleUpdateQuotationStatus}
          onPdf={() => window.open(appendAuthToken(API_BASE_URL + `/api/quotations/${viewingQuotation._id}/pdf`), "_blank", "noopener,noreferrer")}
        />
      )}
    </div>
  );
}

function QuotationViewerModal({ quotation, onClose, onPdf, onStatusChange }) {
  if (!quotation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl rounded-[24px] border border-slate-200 bg-white p-6 shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">Quotation Details</h2>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                quotation.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                quotation.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                quotation.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                "bg-slate-50 text-slate-600 border-slate-200"
              }`}>
                {quotation.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 font-semibold">{quotation.quotationNumber || quotation.quotationNo || "Draft Quotation"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none"
            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "0" }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="mt-4 overflow-y-auto space-y-5 flex-1 pr-1">
          {/* Metadata Cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer Details</h3>
              <div className="text-sm space-y-1">
                <p className="font-bold text-slate-900">{quotation.customerName || "-"}</p>
                {quotation.contactPerson && <p className="text-slate-600"><span className="font-semibold text-slate-500">Contact:</span> {quotation.contactPerson}</p>}
                {quotation.phone && <p className="text-slate-600"><span className="font-semibold text-slate-500">Phone:</span> {quotation.phone}</p>}
                {quotation.email && <p className="text-slate-600"><span className="font-semibold text-slate-500">Email:</span> {quotation.email}</p>}
                {quotation.gstNumber && <p className="text-slate-600"><span className="font-semibold text-slate-500">GST:</span> {quotation.gstNumber}</p>}
                {quotation.billingAddress && <p className="text-slate-600 mt-1 whitespace-pre-wrap"><span className="font-semibold text-slate-500">Billing Address:</span><br/>{quotation.billingAddress}</p>}
              </div>
            </div>

            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quotation Information</h3>
              <div className="text-sm space-y-1">
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Date:</span> {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString("en-GB") : "-"}</p>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString("en-GB") : "-"}</p>
                {quotation.referenceNo && <p className="text-slate-600"><span className="font-semibold text-slate-500">Reference No:</span> {quotation.referenceNo}</p>}
                {quotation.subject && <p className="text-slate-600"><span className="font-semibold text-slate-500">Subject:</span> {quotation.subject}</p>}
                {quotation.deliveryTime && <p className="text-slate-600"><span className="font-semibold text-slate-500">Delivery Time:</span> {quotation.deliveryTime}</p>}
                {quotation.paymentTerms && <p className="text-slate-600"><span className="font-semibold text-slate-500">Payment Terms:</span> {quotation.paymentTerms}</p>}
                {quotation.warranty && <p className="text-slate-600"><span className="font-semibold text-slate-500">Warranty:</span> {quotation.warranty}</p>}
                {(quotation.gstType === "sez_supply" || quotation.lutRefNo) && (
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">LUT:</span> <span className="font-bold text-slate-800">TAX EXEMPTED</span>{quotation.lutRefNo ? ` • ${quotation.lutRefNo}` : ""}</p>
                )}
              </div>
            </div>
          </div>

          {/* Products / Items Table */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1">
              <span>🛒</span> Quoted Products
            </h3>
            <div className="overflow-x-auto rounded-[12px] border border-slate-200 bg-white">
              <table className="w-full min-w-[700px] text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                    <th className="p-2.5 text-center border-r border-slate-100 w-10">#</th>
                    <th className="p-2.5 border-r border-slate-100 w-28">Item Code</th>
                    <th className="p-2.5 border-r border-slate-100 w-24">HSN Code</th>
                    <th className="p-2.5 border-r border-slate-100">Description</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-16">Qty</th>
                    <th className="p-2.5 border-r border-slate-100 w-16">Unit</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">List Price</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Discount</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Unit Price</th>
                    <th className="p-2.5 text-right w-24">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(quotation.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 align-middle">
                      <td className="p-2.5 text-center border-r border-slate-100 text-slate-400 font-semibold">{idx + 1}</td>
                      <td className="p-2.5 border-r border-slate-100 font-mono font-medium text-slate-600 uppercase">{item.itemCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-500">{item.hsnCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-800 font-medium whitespace-pre-wrap">{item.description}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-900 font-semibold">{item.quantity}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-600">{item.unit || "NA"}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-600 font-medium">₹{(item.listPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-red-600 font-medium">
                        {item.discountValue ? (item.discountType === "amount" ? `₹${item.discountValue.toLocaleString("en-IN")}` : `${item.discountValue}%`) : "-"}
                      </td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-700 font-semibold">₹{(item.unitPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right text-slate-900 font-bold">₹{(item.totalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  {(!quotation.items || !quotation.items.length) && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-slate-400">No products listed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Area: Terms & Summary */}
          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-7 rounded-[16px] border border-slate-100 p-4 space-y-3">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terms & Remarks</h4>
              <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
                {quotation.terms && <p><strong>Terms:</strong> {quotation.terms}</p>}
                {quotation.notes && <p><strong>Notes:</strong> {quotation.notes}</p>}
              </div>
            </div>

            <div className="md:col-span-5 rounded-[16px] border border-slate-200 bg-slate-50/50 p-4 space-y-2">
              <div className="flex justify-between text-xs font-bold text-slate-800">
                <span>Sub Total:</span>
                <span className="font-bold text-slate-900">₹{(quotation.subTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500 font-semibold">
                <span>GST:</span>
                <span>₹{(quotation.gstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-slate-800 border-t border-slate-200 pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{(quotation.grandTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 shrink-0">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-600">Update Status:</label>
            <select
              value={quotation.status || "sent"}
              onChange={(e) => onStatusChange(quotation._id, e.target.value)}
              className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-xl bg-white text-slate-800 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted (Won)</option>
              <option value="rejected">Rejected (Lost)</option>
              <option value="expired">Expired</option>
              <option value="invoiced">Invoiced</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPdf}
              className="rounded-[12px] bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <span>📄</span> View PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[12px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
